'use client'

import * as React from 'react'
import { DICTS, LOCALE_COOKIE, type Dictionary, type LocaleCode } from './dict'

// Cookie-based locale. No path-prefix routing — set a cookie, page
// re-renders, dictionary swaps. Lets us ship real translations across
// every page without retrofitting every route under app/[locale]/.
//
// When per-locale Sanity content is ready, the page body can also
// localize via the same cookie (CmsPage already takes a slug; pass
// `${slug}-${locale}` to fetch the matching variant).

const READY_LOCALES: LocaleCode[] = ['en', 'es', 'fr', 'de']

type Ctx = {
  locale: LocaleCode
  setLocale: (l: LocaleCode) => void
  t: Dictionary
}

const I18nContext = React.createContext<Ctx | null>(null)

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`))
  return m ? decodeURIComponent(m[1]) : null
}

function writeCookie(name: string, value: string) {
  if (typeof document === 'undefined') return
  const oneYear = 60 * 60 * 24 * 365
  document.cookie = `${name}=${encodeURIComponent(value)};max-age=${oneYear};path=/;samesite=lax`
}

export function I18nProvider({
  children,
  initialLocale = 'en',
}: {
  children: React.ReactNode
  initialLocale?: LocaleCode
}) {
  const [locale, setLocaleState] = React.useState<LocaleCode>(initialLocale)

  // Pick up the cookie on mount (after hydration). Server renders with
  // `en` so HTML stays cacheable; we swap to the cookie value on the
  // client. Tiny FOUC on first paint for non-English users — acceptable
  // for a marketing site.
  React.useEffect(() => {
    const fromCookie = readCookie(LOCALE_COOKIE)
    if (fromCookie && READY_LOCALES.includes(fromCookie as LocaleCode) && fromCookie !== locale) {
      setLocaleState(fromCookie as LocaleCode)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Reflect on <html lang=…> so screen-readers + Google pick up the
  // right language attribute.
  React.useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = locale
  }, [locale])

  const setLocale = React.useCallback((next: LocaleCode) => {
    if (!READY_LOCALES.includes(next)) return
    writeCookie(LOCALE_COOKIE, next)
    setLocaleState(next)
  }, [])

  const value = React.useMemo<Ctx>(
    () => ({ locale, setLocale, t: DICTS[locale] ?? DICTS.en }),
    [locale, setLocale],
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

/** Read the active dictionary. Components that aren't wrapped in
 *  I18nProvider get the English defaults so static pre-renders keep
 *  working without runtime errors. */
export function useTranslation(): Ctx {
  const ctx = React.useContext(I18nContext)
  if (ctx) return ctx
  return { locale: 'en', setLocale: () => {}, t: DICTS.en }
}

export { READY_LOCALES }
